/**
 * Static UI metadata for each transform step type — labels, icons, dropdown
 * descriptions, tooltip hints, and the section groupings used in the
 * "Add Step" picker. Keep market-research / data-prep examples concrete:
 * users scan these to find the step that maps to the pattern they want.
 */
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  faBroom,
  faCalculator,
  faClone,
  faCodeBranch,
  faColumns,
  faFolderPlus,
  faFolderTree,
  faLayerGroup,
  faObjectGroup,
  faRectangleList,
  faRepeat,
  faRotate,
  faScaleBalanced,
  faSliders,
  faTag,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons"
import { TransformStepType } from "stores/queryBuilderStore"

export const STEP_TYPE_LABELS: Record<TransformStepType, string> = {
  combine_or: "Build NET (OR / any apply)",
  and_combine: "Build NET (AND / all apply)",
  negate: "Invert field (NOT)",
  cleanup: "Fill blank values",
  qtable: "Create container (QTable)",
  field_decl: "Create field (typed variable)",
  append: "Append into container",
  remove: "Remove field or answer option",
  conditional_assign: "Conditional assign (ASSIGN … IF)",
  copy_structure: "Clone container structure (COPYSTRUCT)",
  loop: "FOR loop (iterate)",
  define: "Define a value (DEFINE)",
  appendstruct: "Append structure (APPENDSTRUCT)",
  calc: "Calculate a value (CALC)",
  cat: "Categorise a number (CAT)",
  weighting: "Weighting (apply / calculate)",
}

export const STEP_TYPE_ICONS: Record<TransformStepType, IconDefinition> = {
  combine_or: faObjectGroup,
  and_combine: faLayerGroup,
  negate: faRotate,
  cleanup: faBroom,
  qtable: faFolderPlus,
  field_decl: faColumns,
  append: faFolderTree,
  remove: faTrashCan,
  conditional_assign: faCodeBranch,
  copy_structure: faClone,
  loop: faRepeat,
  define: faTag,
  appendstruct: faRectangleList,
  calc: faCalculator,
  cat: faSliders,
  weighting: faScaleBalanced,
}

/** One-liner shown beneath each item in the Add Step dropdown — concrete
 *  market-research / data-prep examples, not abstract descriptions. */
export const STEP_TYPE_DESCRIPTIONS: Record<TransformStepType, string> = {
  combine_or: "Top-2-Box, Total Awareness, any-of nets.",
  and_combine: "Aware AND Considered, qualified subgroups.",
  negate: "Not aware, did not select.",
  cleanup: "Non-qualifiers read as No, not missing.",
  qtable: "Empty container for banners or nested paths.",
  field_decl: "A typed variable: yes/no, number, or text.",
  append: "Assemble banners and demographics groups.",
  remove: "Trim unused codes, drop 'No' rows.",
  conditional_assign:
    "Subgroup metrics, country-specific blanking, age bands.",
  copy_structure: "Schema-only clone for parallel filtering.",
  loop: "Repeat steps once per value.",
  define: "Reusable name for a value you'll type often.",
  appendstruct: "Add an empty copy of another container's shape.",
  calc: "Derived score, sum of fields, weighted index, banded flag.",
  cat: "Age bands, income brackets, score buckets from a number.",
  weighting: "Apply a weights column, or rim-weight to targets.",
}

/** Section groupings in the Add Step dropdown — purpose-driven, not
 *  alphabetical, so users can scan to the right pattern fast. */
export const STEP_GROUPS: ReadonlyArray<{
  label: string
  steps: TransformStepType[]
}> = [
  {
    label: "Build derived variables",
    steps: [
      "combine_or",
      "and_combine",
      "negate",
      "conditional_assign",
      "calc",
      "cat",
    ],
  },
  {
    label: "Reshape project tree",
    steps: ["qtable", "field_decl", "append", "appendstruct", "copy_structure"],
  },
  {
    label: "Clean up data",
    steps: ["cleanup", "remove"],
  },
  {
    label: "Weighting",
    steps: ["weighting"],
  },
  {
    label: "Loops & values",
    steps: ["loop", "define"],
  },
]

export const STEP_TYPE_HINTS: Record<TransformStepType, string> = {
  combine_or:
    "Yes/no logic via OR. 'Create new field' declares a fresh field and OR-joins the sources into it (Top-2-Box, Total Awareness, 'Any Mobile'). 'OR into existing' skips the declaration and folds new sources into a field built earlier, useful for incrementally extending a NET.",
  and_combine:
    "Yes/no logic via AND. 'Create new field' seeds a fresh field with the first source then AND-joins the rest (intersections like 'Aware AND Considered'). 'AND into existing' tightens an existing yes/no field by ANDing further conditions in.",
  negate:
    "NOT operator. 'Create new (inverted copy)' makes a new field with the inverse of the source (Not aware, Did not select). 'Invert in place' flips the existing field's values directly (true ↔ false), no new field declared.",
  cleanup:
    "Convert null values in an existing field to false, true, or 0 without touching non-null values. Run after building a NET so non-qualifiers read as 'No' rather than missing. Emits BLANKFALSE / BLANKTRUE / BLANKZERO.",
  qtable:
    "Create a QTable: a container node in the tree that holds fields and other containers but no data of its own. Use to seed banners, demographics groups, or nested paths consumed by later steps. Emits QTABLE.",
  field_decl:
    "Declare a typed variable. BOOL is a yes/no flag, INT and REAL are numeric (integer / decimal), TEXT is free text. An optional initial value is assigned to every respondent (e.g. BOOL true seeds the flag to true for everyone, INT 0 seeds a counter, leave blank for null). The name can be a bare label or a path inside an existing container (e.g. 'Age//bucket' creates a new field under Age). Emits FIELD.",
  append:
    "Add existing fields or containers under a parent QTable, in the order you drop them. Used to assemble banners and demographic cuts (e.g. APPEND MyDemographics <<GENDER>>). Emits QTABLE + APPEND per child.",
  remove:
    "Delete a field, container, or specific answer option from the project tree for this run. Common for trimming unused codes (REMOVE <<COUNTRY//Korea>>) or 'No' rows from awareness tables. Does not affect raw data.",
  conditional_assign:
    "Set a target field to a value (true / blank / a copy of another field) only for respondents matching one or more conditions. Underpins subgroup metrics, country-specific blanking, and age-band derivation. Emits FIELD + ASSIGN … IF (with a Temp OR-stack when multiple conditions).",
  loop:
    "Run the inner steps once per value, with the loop variable swapped in. The values can come from a categorical field (its distinct answers) or from a list you type. Use the variable name bare inside <<…>> paths anywhere in the body — it's substituted at run time. Loops can nest.",
  define:
    "Give a value a name so later steps can refer to it by that name. The name substitutes literally wherever it appears in subsequent steps. Useful for long path prefixes you type many times.",
  appendstruct:
    "Add an empty copy of another container's structure (fields + sub-containers, but no data) under a target container. The target must already exist — add a 'Create container (QTable)' step earlier in the pipeline first. Inside a loop, use a per-iteration alias so each pass produces a different child.",
  copy_structure:
    "Create a new QTable that mirrors an existing one's structure with no data copied (a schema-only clone). Use when you need a parallel table to filter, blank, or modify separately. Emits QTABLE + COPYSTRUCT.",
  calc:
    "Compute an expression into a numeric or yes/no field. Build common boolean conditions with the wizard (AND/OR/NOT of field comparisons), or type any formula in the raw box — arithmetic (+ - * /), comparisons (== != < <= > >=), ternary (cond ? a : b) and functions are all supported. 'Create new field' declares the target (INT/REAL/BOOL) first; 'CALC into existing' writes into a field built earlier. Emits FIELD + CALC.",
  cat:
    "Bucket a numeric field into a set of yes/no flags — age bands, income brackets, score buckets. Drag in a category container (a group of yes/no fields that already exists in the data tree); its children auto-populate as the categories. Give the source number field, then set each category's range (1, 18-35, <18, >=10, 56+). Emits CAT, mapping the source value onto the container's existing flags.",
  weighting:
    "Survey weighting. 'Apply existing' activates a weights column you already have (USEWEIGHTS) for every following table. 'Calculate' does rim/rake weighting: pick a weights field name, then add one or more weighting variables (Gender, Age, Region…). Drag in each variable's container and its yes/no categories auto-populate. Give each a target % (per variable they add to 100, or leave some blank and those share what's left). All variables are weighted together in one command, optionally limited to a subgroup, scaled to an average or sum (an average of 1 unless you pick one), then applied automatically. After Apply the step shows each target against what the weights reached. Emits FIELD + CALCWEIGHTS + USEWEIGHTS.",
}
